/*
 * Kept in step with the backend's src/services/media/matroskaAudio.ts (WebM
 * transcripts read the same files on the server); a fix in one belongs in the other.
 */
/**
 * The sound of a WebM or Matroska (MKV) file, read without the rest of it
 * where the file allows.
 *
 * A Matroska file is EBML: the header (Info, Tracks, and a SeekHead pointing
 * at the Cues) comes first, then Clusters of blocks, audio and video
 * interleaved. Sound can only be found by walking the clusters, so a stretch
 * is read cluster by cluster from the nearest cue before it (from the first
 * cluster when there are no cues, as in files recorded in a browser, whose
 * clusters and segment also have no size). Lacing (Xiph, fixed and EBML) is
 * unpacked, so each audio frame becomes one packet at its own place in the
 * file.
 *
 * AAC and MP3 inside Matroska come out as ordinary packet tracks (decoded
 * through ADTS and MP3 streams as for MP4). Opus and Vorbis come out as
 * "Xiph" tracks, decoded by xiphDecode.ts.
 *
 * Times: a block's time is its cluster's Timecode plus its own offset, in
 * TimecodeScale units; the track's CodecDelay is taken off, as players do.
 */

import type { ByteSource } from './byteSource';
import { AacCodec, AudioTrack, MediaReadError, Mp3Codec, PacketTable, parseAudioSpecificConfig } from './isoAudio';

export interface XiphCodec {
  kind: 'opus' | 'vorbis';
  sampleRate: number;
  channels: number;
  /** CodecPrivate: OpusHead, or Vorbis's three headers Xiph-laced. */
  description: Uint8Array;
}

/** An audio track whose codec the browser decodes packet by packet (Opus, Vorbis). */
export type XiphAudioTrack = Omit<AudioTrack, 'codec'> & { codec: XiphCodec };

export interface MatroskaLayout {
  /** Nanoseconds per timestamp unit. */
  timecodeScale: number;
  /** The Segment's data start (cue and seek positions count from here). */
  segmentStart: number;
  segmentEnd: number;
  firstCluster: number;
  trackNumber: number;
  codec: AacCodec | Mp3Codec | XiphCodec;
  /** Ticks (timestamp units) taken off every block time. */
  codecDelay: number;
  /** Ticks per frame from DefaultDuration, when given. */
  defaultDuration: number | null;
  /**
   * From the Info's Duration, in seconds. Browser recordings write none: then
   * it is where the sound in the file's last cluster ends, or null.
   */
  durationSeconds: number | null;
  /** Where the Cues element is, when the SeekHead says. */
  cuesAt: number | null;
  /** Cluster positions (absolute) by time in seconds, once read. */
  cues: Array<{ time: number; position: number }> | null;
}

export type MatroskaTrack = (AudioTrack | XiphAudioTrack) & {
  matroska: MatroskaLayout;
  /** With `collectFrames`: each packet's bytes, in table order. */
  frames?: Uint8Array[];
};

export interface MatroskaReadOptions {
  signal?: AbortSignal;
  /** Presentation seconds the packet table must cover; default: the first second. */
  fromS?: number;
  toS?: number;
  layout?: MatroskaLayout;
  blockBytes?: number;
  /** Refuse (MediaReadError) rather than read more than this many bytes of clusters. */
  maxScanBytes?: number;
  /**
   * Keep each audio frame's bytes as the clusters are walked, so they need
   * not be read again (the video around them would be read with them).
   */
  collectFrames?: boolean;
}

const ID = {
  EBML: 0x1a45dfa3,
  DOC_TYPE: 0x4282,
  SEGMENT: 0x18538067,
  SEEK_HEAD: 0x114d9b74,
  SEEK: 0x4dbb,
  SEEK_ID: 0x53ab,
  SEEK_POSITION: 0x53ac,
  INFO: 0x1549a966,
  TIMECODE_SCALE: 0x2ad7b1,
  DURATION: 0x4489,
  TRACKS: 0x1654ae6b,
  TRACK_ENTRY: 0xae,
  TRACK_NUMBER: 0xd7,
  TRACK_TYPE: 0x83,
  FLAG_ENABLED: 0xb9,
  FLAG_DEFAULT: 0x88,
  CODEC_ID: 0x86,
  CODEC_PRIVATE: 0x63a2,
  CODEC_DELAY: 0x56aa,
  DEFAULT_DURATION: 0x23e383,
  CONTENT_ENCODINGS: 0x6d80,
  AUDIO: 0xe1,
  SAMPLING_FREQUENCY: 0xb5,
  OUTPUT_SAMPLING_FREQUENCY: 0x78b5,
  CHANNELS: 0x9f,
  CUES: 0x1c53bb6b,
  CUE_POINT: 0xbb,
  CUE_TIME: 0xb3,
  CUE_TRACK_POSITIONS: 0xb7,
  CUE_CLUSTER_POSITION: 0xf1,
  CLUSTER: 0x1f43b675,
  TIMECODE: 0xe7,
  SIMPLE_BLOCK: 0xa3,
  BLOCK_GROUP: 0xa0,
  BLOCK: 0xa1,
  BLOCK_DURATION: 0x9b,
};

/** Elements that may sit directly in a Cluster: any other ID ends a Cluster of unknown size. */
const CLUSTER_CHILDREN = [0xe7, 0xa3, 0xa0, 0xa7, 0xab, 0xec, 0xbf, 0x5854, 0xaf];

const corrupt = (what: string) => new MediaReadError('corrupt', `The file's ${what} could not be read.`);
const notMatroska = () => new MediaReadError('not-mp4', 'This file is not a WebM or MKV.');

/** True for the first bytes of a WebM/MKV (an EBML header). */
export const isMatroskaHeader = (head: Uint8Array): boolean =>
  head.length >= 4 && head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3;

// ---------------------------------------------------------------------------
// EBML
// ---------------------------------------------------------------------------

const vintLength = (first: number): number => {
  for (let i = 0; i < 8; i += 1) if (first & (0x80 >> i)) return i + 1;
  return 0;
};

interface ElementHeader {
  id: number;
  /** null: unknown size (runs to the end of its parent). */
  size: number | null;
  start: number;
  dataStart: number;
}

/** An element header in `data` at `at` (offset `base` in the file), or null. */
const parseHeader = (data: Uint8Array, at: number, base: number): ElementHeader | null => {
  if (at >= data.length) return null;
  const idLength = vintLength(data[at]);
  if (idLength === 0 || idLength > 4 || at + idLength >= data.length) return null;
  let id = 0;
  for (let i = 0; i < idLength; i += 1) id = id * 256 + data[at + i];
  const sizeAt = at + idLength;
  const sizeLength = vintLength(data[sizeAt]);
  if (sizeLength === 0 || sizeAt + sizeLength > data.length) return null;
  let size = data[sizeAt] & (0xff >> sizeLength);
  let allOnes = size === 0xff >> sizeLength;
  for (let i = 1; i < sizeLength; i += 1) {
    size = size * 256 + data[sizeAt + i];
    if (data[sizeAt + i] !== 0xff) allOnes = false;
  }
  return { id, size: allOnes ? null : size, start: base + at, dataStart: base + sizeAt + sizeLength };
};

const readUint = (data: Uint8Array, start: number, end: number): number => {
  let value = 0;
  for (let i = start; i < end; i += 1) value = value * 256 + data[i];
  return value;
};

const readFloat = (data: Uint8Array, start: number, end: number): number => {
  const view = new DataView(data.buffer, data.byteOffset + start, end - start);
  if (end - start === 4) return view.getFloat32(0);
  if (end - start === 8) return view.getFloat64(0);
  return 0;
};

const readString = (data: Uint8Array, start: number, end: number): string => {
  let text = '';
  for (let i = start; i < end && data[i] !== 0; i += 1) text += String.fromCharCode(data[i]);
  return text;
};

/** Children of an element held in memory, as [id, dataStart, dataEnd] (positions in `data`). */
const childrenOf = (data: Uint8Array, start: number, end: number): Array<[number, number, number]> => {
  const out: Array<[number, number, number]> = [];
  let at = start;
  while (at < end) {
    const header = parseHeader(data, at, 0);
    if (!header) break;
    const dataEnd = header.size === null ? end : Math.min(end, header.dataStart + header.size);
    out.push([header.id, header.dataStart, dataEnd]);
    at = dataEnd;
  }
  return out;
};

const childValue = (children: Array<[number, number, number]>, id: number) => {
  for (let i = 0; i < children.length; i += 1) if (children[i][0] === id) return children[i];
  return null;
};

/** Reads a file forwards in blocks, keeping the current one. */
class Window {
  private block: Uint8Array = new Uint8Array(0);
  private start = 0;
  fetched = 0;

  constructor(private readonly source: ByteSource, private readonly blockBytes: number, private readonly signal?: AbortSignal) {}

  async bytes(offset: number, length: number): Promise<[Uint8Array, number]> {
    if (offset < this.start || offset + length > this.start + this.block.length) {
      const wanted = Math.min(Math.max(length, this.blockBytes), Math.max(0, this.source.size - offset));
      this.block = wanted > 0 ? await this.source.read(offset, wanted, this.signal) : new Uint8Array(0);
      this.start = offset;
      this.fetched += this.block.length;
    }
    return [this.block, offset - this.start];
  }

  async header(offset: number): Promise<ElementHeader | null> {
    if (offset + 2 > this.source.size) return null;
    const [data, at] = await this.bytes(offset, Math.min(12, this.source.size - offset));
    return parseHeader(data.subarray(0, Math.min(data.length, at + 12)), at, offset - at);
  }

  /** A whole element's payload (bounded). */
  async payload(header: ElementHeader, limit: number, what: string): Promise<Uint8Array> {
    const size = header.size === null ? null : header.size;
    if (size === null || size > limit) throw corrupt(what);
    const [data, at] = await this.bytes(header.dataStart, size);
    if (data.length - at < size) throw corrupt(what);
    return data.subarray(at, at + size);
  }
}

// ---------------------------------------------------------------------------
// The header: Info, Tracks, SeekHead
// ---------------------------------------------------------------------------

const AAC_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];

const trackCodec = (codecId: string, privateData: Uint8Array | null, sampleRate: number, channels: number): MatroskaLayout['codec'] => {
  if (codecId === 'A_OPUS') {
    if (!privateData || privateData.length < 19) throw corrupt('Opus header');
    return { kind: 'opus', sampleRate: 48000, channels: privateData[9] || channels || 1, description: privateData.slice() };
  }
  if (codecId === 'A_VORBIS') {
    if (!privateData || privateData.length < 3) throw corrupt('Vorbis headers');
    return { kind: 'vorbis', sampleRate: sampleRate || 44100, channels: channels || 2, description: privateData.slice() };
  }
  if (codecId.indexOf('A_AAC') === 0) {
    if (privateData && privateData.length >= 2) return parseAudioSpecificConfig(privateData);
    // Old-style IDs carry no config: build AAC-LC's from the track's rate and channels.
    const samplingIndex = AAC_RATES.indexOf(sampleRate);
    if (samplingIndex < 0) throw new MediaReadError('unsupported-audio', 'This AAC sound has an unusual sample rate.');
    return { kind: 'aac', objectType: 2, samplingIndex, channelConfig: channels || 2, sampleRate, channels: channels || 2 };
  }
  if (codecId === 'A_MPEG/L3') return { kind: 'mp3', sampleRate: sampleRate || 44100, channels: channels || 2 };
  throw new MediaReadError('unsupported-audio', `This file’s sound (${codecId.replace(/^A_/, '')}) can’t be read in the browser.`);
};

interface ParsedTrack {
  number: number;
  enabled: boolean;
  isDefault: boolean;
  codecId: string;
  privateData: Uint8Array | null;
  codecDelayNs: number;
  defaultDurationNs: number | null;
  sampleRate: number;
  channels: number;
  encoded: boolean;
}

const parseTracks = (data: Uint8Array): ParsedTrack[] => {
  const tracks: ParsedTrack[] = [];
  const entries = childrenOf(data, 0, data.length);
  for (let e = 0; e < entries.length; e += 1) {
    const [id, start, end] = entries[e];
    if (id !== ID.TRACK_ENTRY) continue;
    const fields = childrenOf(data, start, end);
    const type = childValue(fields, ID.TRACK_TYPE);
    if (!type || readUint(data, type[1], type[2]) !== 2) continue;
    const number = childValue(fields, ID.TRACK_NUMBER);
    const codec = childValue(fields, ID.CODEC_ID);
    if (!number || !codec) continue;
    const enabled = childValue(fields, ID.FLAG_ENABLED);
    const isDefault = childValue(fields, ID.FLAG_DEFAULT);
    const privateData = childValue(fields, ID.CODEC_PRIVATE);
    const delay = childValue(fields, ID.CODEC_DELAY);
    const defaultDuration = childValue(fields, ID.DEFAULT_DURATION);
    const audio = childValue(fields, ID.AUDIO);
    let sampleRate = 0;
    let channels = 0;
    if (audio) {
      const audioFields = childrenOf(data, audio[1], audio[2]);
      const rate = childValue(audioFields, ID.OUTPUT_SAMPLING_FREQUENCY) || childValue(audioFields, ID.SAMPLING_FREQUENCY);
      const count = childValue(audioFields, ID.CHANNELS);
      if (rate) sampleRate = Math.round(readFloat(data, rate[1], rate[2]));
      if (count) channels = readUint(data, count[1], count[2]);
    }
    tracks.push({
      number: readUint(data, number[1], number[2]),
      enabled: enabled ? readUint(data, enabled[1], enabled[2]) !== 0 : true,
      isDefault: isDefault ? readUint(data, isDefault[1], isDefault[2]) !== 0 : true,
      codecId: readString(data, codec[1], codec[2]),
      privateData: privateData ? data.slice(privateData[1], privateData[2]) : null,
      codecDelayNs: delay ? readUint(data, delay[1], delay[2]) : 0,
      defaultDurationNs: defaultDuration ? readUint(data, defaultDuration[1], defaultDuration[2]) : null,
      sampleRate,
      channels,
      encoded: Boolean(childValue(fields, ID.CONTENT_ENCODINGS)),
    });
  }
  return tracks;
};

const parseCues = (data: Uint8Array, segmentStart: number, scaleNs: number): Array<{ time: number; position: number }> => {
  const cues: Array<{ time: number; position: number }> = [];
  const points = childrenOf(data, 0, data.length);
  for (let p = 0; p < points.length; p += 1) {
    if (points[p][0] !== ID.CUE_POINT) continue;
    const fields = childrenOf(data, points[p][1], points[p][2]);
    const time = childValue(fields, ID.CUE_TIME);
    const positions = childValue(fields, ID.CUE_TRACK_POSITIONS);
    if (!time || !positions) continue;
    const cluster = childValue(childrenOf(data, positions[1], positions[2]), ID.CUE_CLUSTER_POSITION);
    if (!cluster) continue;
    cues.push({
      time: (readUint(data, time[1], time[2]) * scaleNs) / 1e9,
      position: segmentStart + readUint(data, cluster[1], cluster[2]),
    });
  }
  return cues.sort((a, b) => a.time - b.time);
};

const TAIL_BYTES = 2 * 1024 * 1024;

/**
 * Where the sound ends, in presentation seconds, from the last cluster in
 * the file's final few megabytes that holds audio; null when none is found.
 */
const soundEndFromTail = async (
  source: ByteSource,
  firstCluster: number,
  trackNumber: number,
  scaleNs: number,
  codecDelay: number,
  signal?: AbortSignal
): Promise<number | null> => {
  const from = Math.max(firstCluster, source.size - TAIL_BYTES);
  if (from >= source.size) return null;
  const tail = await source.read(from, source.size - from, signal);
  for (let at = tail.length - 4; at >= 0; at -= 1) {
    if (tail[at] !== 0x1f || tail[at + 1] !== 0x43 || tail[at + 2] !== 0xb6 || tail[at + 3] !== 0x75) continue;
    const cluster = parseHeader(tail, at, from);
    if (!cluster) continue;
    const start = cluster.dataStart - from;
    const end = cluster.size === null ? tail.length : Math.min(tail.length, start + cluster.size);
    let clusterTime: number | null = null;
    let last: number | null = null;
    for (let position = start; position < end;) {
      const child = parseHeader(tail, position, from);
      if (!child) break;
      if (cluster.size === null && CLUSTER_CHILDREN.indexOf(child.id) < 0) break;
      let childStart = child.dataStart - from;
      let childEnd = child.size === null ? end : Math.min(end, childStart + child.size);
      const next = childEnd;
      if (child.id === ID.TIMECODE) {
        clusterTime = readUint(tail, childStart, childEnd);
      } else if ((child.id === ID.SIMPLE_BLOCK || child.id === ID.BLOCK_GROUP) && clusterTime !== null) {
        if (child.id === ID.BLOCK_GROUP) {
          const block = childrenOf(tail, childStart, childEnd).filter((part) => part[0] === ID.BLOCK)[0];
          if (!block) {
            position = next;
            continue;
          }
          childStart = block[1];
          childEnd = block[2];
        }
        const trackLength = vintLength(tail[childStart]);
        if (trackLength > 0 && childStart + trackLength + 2 < childEnd) {
          let track = tail[childStart] & (0xff >> trackLength);
          for (let i = 1; i < trackLength; i += 1) track = track * 256 + tail[childStart + i];
          if (track === trackNumber) {
            const relative = (((tail[childStart + trackLength] << 8) | tail[childStart + trackLength + 1]) << 16) >> 16;
            last = Math.max(last ?? -Infinity, clusterTime + relative);
          }
        }
      }
      position = next;
    }
    // A packet's length past its start: near enough for a length.
    if (last !== null) return Math.max(0, ((last - codecDelay) * scaleNs) / 1e9 + 0.02);
  }
  return null;
};

/** Reads the header of a WebM/MKV: its timing, its audio track and where the clusters and cues are. */
export const readMatroskaLayout = async (source: ByteSource, signal?: AbortSignal): Promise<MatroskaLayout> => {
  const window = new Window(source, 256 * 1024, signal);
  const ebml = await window.header(0);
  if (!ebml || ebml.id !== ID.EBML || ebml.size === null) throw notMatroska();
  const docType = childValue(childrenOf(await window.payload(ebml, 4096, 'header'), 0, ebml.size), ID.DOC_TYPE);
  if (docType) {
    const header = await window.payload(ebml, 4096, 'header');
    const type = readString(header, docType[1], docType[2]);
    if (type !== 'webm' && type !== 'matroska') throw notMatroska();
  }
  const segment = await window.header(ebml.dataStart + ebml.size);
  if (!segment || segment.id !== ID.SEGMENT) throw corrupt('segment');
  const segmentStart = segment.dataStart;
  const segmentEnd = segment.size === null ? source.size : Math.min(source.size, segment.dataStart + segment.size);

  let scaleNs = 1000000;
  let durationTicks: number | null = null;
  let tracks: ParsedTrack[] | null = null;
  let cuesAt: number | null = null;
  let tracksAt: number | null = null;
  let infoAt: number | null = null;
  let cues: Array<{ time: number; position: number }> | null = null;
  let cuesData: Uint8Array | null = null;
  let firstCluster = -1;

  const readInfo = (data: Uint8Array) => {
    const fields = childrenOf(data, 0, data.length);
    const scale = childValue(fields, ID.TIMECODE_SCALE);
    const duration = childValue(fields, ID.DURATION);
    if (scale) scaleNs = readUint(data, scale[1], scale[2]) || 1000000;
    if (duration) durationTicks = readFloat(data, duration[1], duration[2]) || null;
  };

  let offset = segmentStart;
  for (let count = 0; count < 256 && offset < segmentEnd; count += 1) {
    const header = await window.header(offset);
    if (!header) break;
    if (header.id === ID.CLUSTER) {
      firstCluster = header.start;
      break;
    }
    if (header.size === null) throw corrupt('header');
    if (header.id === ID.INFO) readInfo(await window.payload(header, 1024 * 1024, 'info'));
    else if (header.id === ID.TRACKS) tracks = parseTracks(await window.payload(header, 4 * 1024 * 1024, 'tracks'));
    else if (header.id === ID.CUES) cuesData = (await window.payload(header, 16 * 1024 * 1024, 'cues')).slice();
    else if (header.id === ID.SEEK_HEAD) {
      const data = await window.payload(header, 1024 * 1024, 'seek head');
      const seeks = childrenOf(data, 0, data.length);
      for (let s = 0; s < seeks.length; s += 1) {
        if (seeks[s][0] !== ID.SEEK) continue;
        const fields = childrenOf(data, seeks[s][1], seeks[s][2]);
        const seekId = childValue(fields, ID.SEEK_ID);
        const position = childValue(fields, ID.SEEK_POSITION);
        if (!seekId || !position) continue;
        const target = readUint(data, seekId[1], seekId[2]);
        const at = segmentStart + readUint(data, position[1], position[2]);
        if (target === ID.CUES) cuesAt = at;
        else if (target === ID.TRACKS) tracksAt = at;
        else if (target === ID.INFO) infoAt = at;
      }
    }
    offset = header.dataStart + header.size;
  }
  // Info and Tracks the seek head points past the first cluster to (rare).
  if (!tracks && tracksAt !== null) {
    const header = await window.header(tracksAt);
    if (header && header.id === ID.TRACKS) tracks = parseTracks(await window.payload(header, 4 * 1024 * 1024, 'tracks'));
  }
  if (infoAt !== null && durationTicks === null) {
    const header = await window.header(infoAt);
    if (header && header.id === ID.INFO) readInfo(await window.payload(header, 1024 * 1024, 'info'));
  }
  if (!tracks) throw corrupt('track list');
  if (firstCluster < 0) throw new MediaReadError('no-audio', 'This file has no sound.');
  const audio = tracks.filter((track) => track.enabled).sort((a, b) => Number(b.isDefault) - Number(a.isDefault))[0];
  if (!audio) throw new MediaReadError('no-audio', 'This video has no sound track.');
  if (audio.encoded) {
    throw new MediaReadError('unsupported-audio', 'This file’s sound is stored compressed inside the container, which the browser can’t read.');
  }
  if (cuesData) cues = parseCues(cuesData, segmentStart, scaleNs);
  const finalDuration = durationTicks as number | null;
  const codecDelay = audio.codecDelayNs / scaleNs;
  // Browser recordings write no Duration: take it from the last cluster's sound.
  const tailSeconds = finalDuration === null
    ? await soundEndFromTail(source, firstCluster, audio.number, scaleNs, codecDelay, signal)
    : null;
  return {
    timecodeScale: scaleNs,
    segmentStart,
    segmentEnd,
    firstCluster,
    trackNumber: audio.number,
    codec: trackCodec(audio.codecId, audio.privateData, audio.sampleRate, audio.channels),
    codecDelay,
    defaultDuration: audio.defaultDurationNs !== null ? audio.defaultDurationNs / scaleNs : null,
    durationSeconds: finalDuration !== null ? (finalDuration * scaleNs) / 1e9 : tailSeconds,
    cuesAt,
    cues,
  };
};

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

interface AudioBlock {
  /** Block time in ticks. */
  time: number;
  offsets: number[];
  sizes: number[];
  /** BlockDuration in ticks, when a BlockGroup gives it. */
  duration: number | null;
  /** The frames' bytes, when collected. */
  payloads?: Uint8Array[];
}

/** Frame sizes of a laced block, from the lace header at `at` (just after the flags). */
const laceSizes = (data: Uint8Array, at: number, end: number, lacing: number): { sizes: number[]; dataStart: number } => {
  const count = data[at] + 1;
  let position = at + 1;
  const sizes: number[] = [];
  if (lacing === 1) {
    // Xiph: each size is a run of 255s and a final byte.
    for (let i = 0; i < count - 1; i += 1) {
      let size = 0;
      for (;;) {
        if (position >= end) throw corrupt('block');
        const byte = data[position];
        position += 1;
        size += byte;
        if (byte !== 255) break;
      }
      sizes.push(size);
    }
  } else if (lacing === 3) {
    // EBML: the first size, then signed differences.
    const readVint = (signed: boolean): number => {
      const length = vintLength(data[position]);
      if (length === 0 || position + length > end) throw corrupt('block');
      let value = data[position] & (0xff >> length);
      for (let i = 1; i < length; i += 1) value = value * 256 + data[position + i];
      position += length;
      return signed ? value - (Math.pow(2, 7 * length - 1) - 1) : value;
    };
    let size = readVint(false);
    sizes.push(size);
    for (let i = 1; i < count - 1; i += 1) {
      size += readVint(true);
      sizes.push(size);
    }
  }
  const remaining = end - position;
  if (lacing === 2) {
    const each = Math.floor(remaining / count);
    for (let i = 0; i < count; i += 1) sizes.push(each);
    return { sizes, dataStart: position };
  }
  let used = 0;
  for (let i = 0; i < sizes.length; i += 1) used += sizes[i];
  if (used > remaining) throw corrupt('block');
  sizes.push(remaining - used);
  return { sizes, dataStart: position };
};

/** The audio frames of one Block/SimpleBlock spanning [start, end) in the file, or null for another track's. */
const readBlock = async (
  window: Window,
  start: number,
  end: number,
  trackNumber: number,
  clusterTime: number
): Promise<AudioBlock | null> => {
  const headerBytes = Math.min(end - start, 8);
  let [data, at] = await window.bytes(start, headerBytes);
  const trackLength = vintLength(data[at]);
  if (trackLength === 0 || trackLength + 3 > end - start) throw corrupt('block');
  let track = data[at] & (0xff >> trackLength);
  for (let i = 1; i < trackLength; i += 1) track = track * 256 + data[at + i];
  if (track !== trackNumber) return null;
  // A signed 16-bit offset from the cluster's time.
  const relative = (((data[at + trackLength] << 8) | data[at + trackLength + 1]) << 16) >> 16;
  const flags = data[at + trackLength + 2];
  const lacing = (flags >> 1) & 3;
  const time = clusterTime + relative;
  const headerEnd = start + trackLength + 3;
  if (lacing === 0) return { time, offsets: [headerEnd], sizes: [end - headerEnd], duration: null };
  // The lace header: at most a few bytes per frame.
  [data, at] = await window.bytes(start, Math.min(end - start, 8192));
  const base = start - at;
  const { sizes, dataStart } = laceSizes(data, headerEnd - base, Math.min(data.length, end - base), lacing);
  const offsets: number[] = [];
  let position = dataStart + base;
  for (let i = 0; i < sizes.length; i += 1) {
    offsets.push(position);
    position += sizes[i];
  }
  return { time, offsets, sizes, duration: null };
};

/** An Opus packet's length in 48 kHz samples, from its TOC byte (RFC 6716 §3.1). */
export const opusPacketSamples = (toc: number, second?: number): number => {
  const config = toc >> 3;
  let frame: number;
  if (config < 12) frame = [480, 960, 1920, 2880][config & 3];
  else if (config < 16) frame = (config & 1) === 0 ? 480 : 960;
  else frame = [120, 240, 480, 960][config & 3];
  const code = toc & 3;
  const frames = code === 0 ? 1 : code === 3 ? (second ?? 0) & 0x3f : 2;
  return frame * frames;
};

// ---------------------------------------------------------------------------
// Reading a stretch
// ---------------------------------------------------------------------------

const LEAD_SECONDS = 1;
const TAIL_SECONDS = 1;

/**
 * The audio track, with the packets of presentation seconds [fromS, toS]
 * (plus a second either side). Without fromS/toS: the header and about the
 * first second.
 */
export const readMatroskaTrack = async (source: ByteSource, options: MatroskaReadOptions = {}): Promise<MatroskaTrack> => {
  const { signal } = options;
  const layout = options.layout ?? (await readMatroskaLayout(source, signal));
  const scale = layout.timecodeScale;
  const ticksPerSecond = 1e9 / scale;
  const fromS = Math.max(0, options.fromS ?? 0);
  const toS = options.toS ?? 0;
  const window = new Window(source, options.blockBytes ?? 1024 * 1024, signal);

  // Start from the last cue at least a second before the stretch.
  let start = layout.firstCluster;
  if (fromS > LEAD_SECONDS + 1) {
    let cues = layout.cues;
    if (!cues && layout.cuesAt !== null) {
      const header = await window.header(layout.cuesAt);
      if (header && header.id === ID.CUES) {
        cues = parseCues((await window.payload(header, 16 * 1024 * 1024, 'cues')).slice(), layout.segmentStart, scale);
        layout.cues = cues;
      }
    }
    if (cues) {
      for (let i = 0; i < cues.length; i += 1) {
        if (cues[i].time <= fromS - LEAD_SECONDS && cues[i].position >= layout.firstCluster) start = cues[i].position;
      }
      const check = await window.header(start);
      if (!check || check.id !== ID.CLUSTER) start = layout.firstCluster;
    }
  }

  const blocks: AudioBlock[] = [];
  const wantFrom = (fromS - LEAD_SECONDS) * ticksPerSecond + layout.codecDelay;
  const wantTo = (toS + TAIL_SECONDS) * ticksPerSecond + layout.codecDelay;
  const maxScan = options.maxScanBytes ?? Infinity;
  const fetchedBefore = window.fetched;
  let reachedEnd = false;
  let lastSeen = 0;
  let position = start;
  scan: for (;;) {
    if (position >= layout.segmentEnd) {
      reachedEnd = true;
      break;
    }
    const cluster = await window.header(position);
    if (!cluster) {
      reachedEnd = true;
      break;
    }
    if (cluster.id !== ID.CLUSTER) {
      // Cues, tags and the like between or after the clusters.
      if (cluster.size === null) {
        reachedEnd = true;
        break;
      }
      position = cluster.dataStart + cluster.size;
      continue;
    }
    const clusterEnd = cluster.size === null ? layout.segmentEnd : Math.min(layout.segmentEnd, cluster.dataStart + cluster.size);
    let clusterTime = 0;
    let at = cluster.dataStart;
    while (at < clusterEnd) {
      if (window.fetched - fetchedBefore > maxScan) {
        throw new MediaReadError('unsupported-audio', 'This file’s sound can’t be found without downloading too much of it.');
      }
      const child = await window.header(at);
      if (!child) break;
      if (cluster.size === null && CLUSTER_CHILDREN.indexOf(child.id) < 0) break;
      const childEnd = child.size === null ? clusterEnd : Math.min(clusterEnd, child.dataStart + child.size);
      if (child.id === ID.TIMECODE) {
        const [data, index] = await window.bytes(child.dataStart, childEnd - child.dataStart);
        clusterTime = readUint(data, index, index + (childEnd - child.dataStart));
      } else if (child.id === ID.SIMPLE_BLOCK || child.id === ID.BLOCK_GROUP) {
        let block: AudioBlock | null = null;
        if (child.id === ID.SIMPLE_BLOCK) {
          block = await readBlock(window, child.dataStart, childEnd, layout.trackNumber, clusterTime);
        } else {
          let duration: number | null = null;
          let inner = child.dataStart;
          while (inner < childEnd) {
            const part = await window.header(inner);
            if (!part || part.size === null) break;
            const partEnd = Math.min(childEnd, part.dataStart + part.size);
            if (part.id === ID.BLOCK) block = await readBlock(window, part.dataStart, partEnd, layout.trackNumber, clusterTime);
            else if (part.id === ID.BLOCK_DURATION) {
              const [data, index] = await window.bytes(part.dataStart, partEnd - part.dataStart);
              duration = readUint(data, index, index + (partEnd - part.dataStart));
            }
            inner = partEnd;
          }
          if (block) block.duration = duration;
        }
        if (block) {
          lastSeen = Math.max(lastSeen, block.time);
          if (block.time >= wantFrom) {
            if (options.collectFrames) {
              const payloads: Uint8Array[] = [];
              for (let f = 0; f < block.offsets.length; f += 1) {
                const [data, index] = await window.bytes(block.offsets[f], block.sizes[f]);
                payloads.push(data.slice(index, index + block.sizes[f]));
              }
              block.payloads = payloads;
            }
            blocks.push(block);
          }
          // One block past the stretch gives the last one its length.
          if (block.time > wantTo) break scan;
        }
      }
      at = childEnd;
    }
    position = cluster.size === null ? at : clusterEnd;
  }

  // Each frame's time and length: a block spans until the next block; laced frames share it evenly.
  blocks.sort((a, b) => a.time - b.time);
  const times: number[] = [];
  const durations: number[] = [];
  const offsets: number[] = [];
  const sizes: number[] = [];
  const frames: Uint8Array[] = [];
  let lastSpan: number | null = null;
  for (let b = 0; b < blocks.length; b += 1) {
    const block = blocks[b];
    let span: number | null = b + 1 < blocks.length ? blocks[b + 1].time - block.time : null;
    if (span === null) {
      // The file's last block. Decoders give its packets' whole length, while
      // its BlockDuration is what is shown after end padding is discarded,
      // so the length comes from the codec where it can.
      const frames = block.offsets.length;
      const codec = layout.codec;
      if (codec.kind === 'opus') {
        const [data, index] = await window.bytes(block.offsets[0], 2);
        span = ((opusPacketSamples(data[index], data[index + 1]) * frames) / 48000) * ticksPerSecond;
      } else if (codec.kind === 'aac') {
        span = ((1024 * frames) / codec.sampleRate) * ticksPerSecond;
      } else if (codec.kind === 'mp3') {
        span = (((codec.sampleRate >= 32000 ? 1152 : 576) * frames) / codec.sampleRate) * ticksPerSecond;
      } else {
        span = lastSpan ?? block.duration ?? (layout.defaultDuration !== null ? layout.defaultDuration * frames : null);
      }
    }
    if (span === null || !(span > 0)) span = lastSpan ?? ticksPerSecond * 0.02;
    lastSpan = span;
    for (let f = 0; f < block.offsets.length; f += 1) {
      times.push(block.time + (span * f) / block.offsets.length);
      durations.push(span / block.offsets.length);
      offsets.push(block.offsets[f]);
      sizes.push(block.sizes[f]);
      if (block.payloads) frames.push(block.payloads[f]);
    }
  }
  const table: PacketTable = {
    kind: 'packets',
    count: times.length,
    times: Float64Array.from(times),
    durations: Float64Array.from(durations),
    offsets: Float64Array.from(offsets),
    sizes: Uint32Array.from(sizes),
  };
  const lastEnd = table.count > 0
    ? (table.times[table.count - 1] + table.durations[table.count - 1] - layout.codecDelay) / ticksPerSecond
    : (lastSeen - layout.codecDelay) / ticksPerSecond;
  let durationSeconds = layout.durationSeconds;
  if (durationSeconds === null) durationSeconds = reachedEnd ? Math.max(0, lastEnd) : Math.max(lastEnd, toS);
  const base = {
    timescale: ticksPerSecond,
    mediaStart: layout.codecDelay,
    startDelay: 0,
    table,
    fragmented: false,
    durationSeconds,
    complete: reachedEnd || layout.durationSeconds !== null,
    matroska: layout,
    ...(options.collectFrames ? { frames } : {}),
  };
  return { ...base, codec: layout.codec } as MatroskaTrack;
};

/**
 * A source that serves a stretch's collected frames from memory. The reads
 * made for them join nearby frames, gaps included (other tracks' bytes,
 * which nothing uses): those come back as zeros. A read that touches no
 * collected frame goes to the file.
 */
export const frameBackedSource = (source: ByteSource, table: PacketTable, frames: Uint8Array[]): ByteSource => {
  const order: number[] = [];
  for (let i = 0; i < table.count && i < frames.length; i += 1) order.push(i);
  order.sort((a, b) => table.offsets[a] - table.offsets[b]);
  return {
    size: source.size,
    async read(offset, length, signal) {
      const start = Math.max(0, offset);
      const end = Math.min(source.size, offset + length);
      // The first frame that ends after `start`.
      let low = 0;
      let high = order.length;
      while (low < high) {
        const middle = (low + high) >> 1;
        const i = order[middle];
        if (table.offsets[i] + frames[i].length <= start) low = middle + 1;
        else high = middle;
      }
      if (low >= order.length || table.offsets[order[low]] >= end) return source.read(offset, length, signal);
      const out = new Uint8Array(Math.max(0, end - start));
      for (let k = low; k < order.length && table.offsets[order[k]] < end; k += 1) {
        const i = order[k];
        const frameStart = table.offsets[i];
        const from = Math.max(start, frameStart);
        const to = Math.min(end, frameStart + frames[i].length);
        if (to > from) out.set(frames[i].subarray(from - frameStart, to - frameStart), from - start);
      }
      return out;
    },
  };
};

export const isXiphTrack = (track: { codec: { kind: string } }): track is XiphAudioTrack =>
  track.codec.kind === 'opus' || track.codec.kind === 'vorbis';

export const isMatroskaTrack = (track: object): track is MatroskaTrack => Boolean((track as MatroskaTrack).matroska);
